import React, { useState } from 'react';
import { useLocation } from 'react-use';
import { navigate } from 'gatsby';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { Button } from '../../../../shadcn/Button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '../../../../shadcn/Dialog';
import { Input } from '../../../../shadcn/Input';
import { Label } from '../../../../shadcn/Label';
import axios from 'axios';
import { useUiComponentsInternationalContext } from '@repo/static-translations';
import { useLocalizedYup } from '../../../../hooks';

interface Props {
  block: {
    linkText: string;
  };
}

export default function SubscriptionActivationDialog({
  block,
}: Readonly<Props>) {
  const location = useLocation();
  const { tt } = useUiComponentsInternationalContext();
  const yup = useLocalizedYup();

  const searchParams = new URLSearchParams(location.search);
  const [formErrors, setFormErrors] = useState(null);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const validationSchema = yup.object().shape({
    activationCode: yup
      .string()
      .required(
        tt(s => s.subscriptionActivationDialog.activationCodeRequiredMessage)
      ),
    email: yup
      .string()
      .email(tt(s => s.subscriptionActivationDialog.emailInvalidMessage))
      .required(tt(s => s.subscriptionActivationDialog.emailRequiredMessage)),
  });

  const iotUserSchema = yup.object({
    email: yup
      .string()
      .trim()
      .email(tt(s => s.subscriptionActivationDialog.emailValidRequiredMessage))
      .required(),
    iotUserId: yup.string(),
    iotGroupId: yup.string(),
    deviceId: yup.string(),
    collarId: yup.string(),
    thingName: yup.string(),
    petName: yup.string().required(),
    petId: yup.string(),
    customerAccessToken: yup.string(),
    customerAccessTokenExpiresAt: yup.string(),
  });

  function iotUserFromToken(token) {
    try {
      if (typeof window === 'undefined') {
        return null;
      }
      const decodedString = decodeURIComponent(atob(token));

      // Remove non-printable characters
      const cleanString = decodedString.replace(/[^\x20-\x7E]/g, '');

      const userInput = JSON.parse(cleanString);

      return iotUserSchema.validateSync(userInput);
    } catch (err) {
      console.error(err);
    }
    return null;
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const handleActivation = async ({ activationCode, email }, event) => {
    event.preventDefault();
    setSubmitDisabled(true);
    setIsLoading(true);

    try {
      const url = process.env.GATSBY_RSC_REDEEM_COSTCO_URL;
      const data = JSON.stringify({
        email,
        code: activationCode,
      });

      const config = {
        url,
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json',
        },
        data,
      };

      await axios.request(config).then(() => {
        setSubmitDisabled(false);
        setIsLoading(false);
        setOpen(false);
        navigate(`/activation-code-thank-you`);
      });
    } catch (error) {
      setFormErrors(error?.response?.data?.error);
      setSubmitDisabled(false);
      setIsLoading(false);
    }
  };

  return (
    <div className="flex container mx-auto max-w-[1680px] py-8 px-6 md:px-10 lg:px-20 xl:px-20 2xl:px-20">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild className="flex flex-col">
          <div>
            <Button variant="link" className="p-0">
              {block?.linkText}
            </Button>
          </div>
        </DialogTrigger>
        <DialogContent className="flex flex-col max-w-lg p-6">
          <DialogTitle /> {/* Needed for screen reader accessibility */}
          {formErrors && (
            <p className="mb-0 text-sm text-destructive font-medium">
              {formErrors}
            </p>
          )}
          <form
            name="activationCodeForm"
            className="flex flex-col gap-6"
            onSubmit={handleSubmit(handleActivation)}
          >
            <div className="flex flex-col gap-2">
              <Label htmlFor="activationCode" className="flex">
                {tt(s => s.subscriptionActivationDialog.activationCodeLabel)}
              </Label>
              <Input
                id="activationCode"
                type="text"
                {...register('activationCode')}
                className="flex border-color-border"
              />
              {errors.activationCode && (
                <p className="text-destructive">
                  {errors?.activationCode?.message.toString()}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="email" className="flex">
                {tt(s => s.subscriptionActivationDialog.emailCodeLabel)}
              </Label>
              <Input
                id="email"
                type="email"
                aria-describedby="activation-code-email"
                defaultValue={
                  iotUserFromToken(searchParams.get('token'))?.email || ''
                }
                {...register('email')}
                className="flex border-color-border"
              />
              {errors.email && (
                <p className="text-destructive">
                  {errors?.email?.message.toString()}
                </p>
              )}
            </div>
            <Button
              type="submit"
              aria-label={tt(
                s =>
                  s.subscriptionActivationDialog.applyActivationButtonAriaLabel
              )}
              disabled={submitDisabled}
              variant="secondary"
              className="flex py-block px-inline gap-2 w-auto h-11 max-w-[83px]"
            >
              {isLoading ? (
                <svg
                  aria-hidden="true"
                  role="status"
                  className="inline w-5 h-5 text-gray-200 animate-spin shrink-0"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="white/80"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    className="fill-cta-foreground"
                  />
                </svg>
              ) : (
                <>{tt(s => s.subscriptionActivationDialog.applyButton)}</>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
